import type { ReactNode } from "react";
import { SlideHeader } from "../../../SlideHeader";
import { DesktopOnly } from "../../../DesktopOnly";

// The published build manual (A7Y-3306). The in-house card cites it rather
// than asserting the difficulty: an estimate an investor can go read beats an
// opinion they can discount.
const DIY_MANUAL_URL = "https://www.apptonomy.ai/diy";

export default function CompetitiveLandscape() {
  return (
    <div className="flex w-full flex-col px-6 pt-8 pb-4 text-slate-900 md:h-full md:px-24 md:pt-16 md:pb-8 md:[&_header]:mb-5">
      {/* No h1 (TPF, 2026-09-09) — the four rows make the argument
          themselves; a headline over them was restating the obvious. */}
      <SlideHeader tab eyebrow="Competitive Landscape" />

      <div className="flex flex-col gap-3 md:flex-1 md:justify-between md:gap-2.5">
        {/* Row 1 — Incumbents (50% width) + barrier arrow overlay */}
        <DesktopOnly>
        <div className="relative flex items-stretch">
          <div className="md:basis-1/2">
            <Card
              title="Incumbents"
              examples="AppTweak · Sensor Tower · AppMagic · Mobile Action · AppFollow"
              headline="Data dashboards. Point solutions."
            >
              <Bullet tone="pro">10–15 years of keyword data · enterprise relationships</Bullet>
              <Bullet tone="con">Codebase inertia; a culture built for stability, not innovation</Bullet>
            </Card>
          </div>
          <div className="hidden md:block md:basis-1/2"></div>
          <div className="pointer-events-none absolute inset-0 hidden md:block">
            <BarrierArrow
              direction="down"
              label="A pivot to end-to-end ASO O.S. is a multi-year rebuild that also confronts the innovator's dilemma."
            />
          </div>
        </div>

        {/* Row 2 — Apptonomy (66% width) + Future arrow */}
        <div className="flex items-stretch gap-3 md:gap-4">
          <div className="md:basis-3/4">
            <Card
              emphasized
              logo
              title="Apptonomy"
              headline="The Constant ASO Platform."
            >
              <Bullet>
                <Lead>Human-led ASO, AI-scaled</Lead> — practitioners author the
                methodology; AI runs it daily across your whole portfolio.
              </Bullet>
              <Bullet>
                <Lead>End-to-end platform</Lead> — audit to publish to measure
                in one workflow, not five stitched-together tools.
              </Bullet>
              <Bullet>
                <Lead>Insights → Actions → Impact</Lead> — the closed loop, on
                the customer's own store data. Every revision is measured.
              </Bullet>
            </Card>
          </div>
          <div className="flex md:basis-1/4">
            <FutureArrow />
          </div>
        </div>

        {/* Row 3 — Upstarts (50% width) + barrier arrow overlay */}
        <div className="relative flex items-stretch">
          <div className="md:basis-1/2">
            <Card
              title="Upstarts"
              examples="RespectASO · Sonar · IconikAI · GrowASO · appeak.pro"
              headline="AI-native. Single-task."
            >
              <Bullet tone="pro">AI-native and quick to demo, no legacy baggage</Bullet>
              <Bullet tone="con">A feature, not the loop — the ASO judgment is the model's</Bullet>
              <Bullet tone="con">Nothing proprietary compounds</Bullet>
            </Card>
          </div>
          <div className="hidden md:block md:basis-1/2"></div>
          <div className="pointer-events-none absolute inset-0 hidden md:block">
            <BarrierArrow
              direction="up"
              label="To compete they must become a platform — author the methodology, integrate the loop, earn a data flywheel — the unglamorous build a wrapper skips, while our lead compounds."
            />
          </div>
        </div>

        {/* Row 4 — "Do It Ourselves": the build-vs-buy option investors ask
            about, at both scales a buyer actually faces. The chat-window rung
            was folded in from The Solution (TPF, 2026-09-08), where it made
            the same point twice; this slide is where the alternative belongs.
            NOT a Card, and deliberately so (TPF, 2026-09-02): the three above
            are competitors in the market, this is the option the reader
            themselves holds. A rule and full-bleed text set it outside that
            set rather than ranking it as a fourth player. */}
        </DesktopOnly>

        <div className="-mb-4 mt-1 rounded-t-2xl bg-slate-900 px-5 py-4 text-white md:-mb-8 md:mt-0 md:px-8 md:py-4">
          <p className="flex flex-wrap items-baseline gap-x-2.5">
            <span className="text-xl font-bold leading-tight tracking-tight text-blue-300 md:text-2xl">
              &ldquo;Do It Ourselves&rdquo;
            </span>
            <span className="text-base italic leading-snug text-slate-300 md:text-lg">
              Top publishers, agencies and indie devs weigh it — proof the pain
              is worth a budget.
            </span>
          </p>
          <div className="mt-1.5 grid grid-cols-1 gap-x-8 gap-y-1 text-sm leading-snug text-slate-300 md:grid-cols-2 md:text-base">
            <p>
              <span className="font-semibold text-white">
                With a chat window
              </span>{" "}
              — one audit is ~100 hand-crafted prompts across several models,
              every day, per app, per store. And it can&rsquo;t read your store
              console, track how you surface in LLM answers, or publish a
              revision back.
            </p>
            <p>
              <span className="font-semibold text-white">
                With an engineering team
              </span>{" "}
              — 12–18 months, 3–4 engineers, permanent upkeep. Most would balk
              at the true scope, so we published the manual rather than assert
              it:{" "}
              <a
                href={DIY_MANUAL_URL}
                target="_blank"
                rel="noreferrer"
                className="font-semibold text-blue-300 underline decoration-blue-500 underline-offset-2 hover:decoration-blue-300"
              >
                apptonomy.ai/diy
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function BarrierArrow({
  direction,
  label,
}: {
  direction: "down" | "up";
  label: string;
}) {
  const isDown = direction === "down";
  // ViewBox uses actual pixel dimensions (1088 × 194 ≈ row size) so user-space
  // ≈ pixels (no anisotropic scaling, marker not distorted) and we can reason
  // about Apptonomy crossing in actual pixel terms.
  //
  // Apptonomy occupies x ∈ [0, 804], y ∈ [210, 404] in row-1 local pixels
  // (row 2 sits 16px below row 1). For the line NOT to cross Apptonomy, the
  // line must reach y=210 (row-2 top) at x > 804.
  //
  // Slope = (215-97)/(950-544) = 0.291. At x=804, y ≈ 173 (well above
  // row-2 top); line enters row 2 only at x ≈ 932 (well past Apptonomy's
  // right edge at 804). Endpoint y=215 lands just inside row 2's upper area
  // at slide x ≈ 87%, near the blue arrow's body. Symmetric for the up arrow.
  return (
    <div className="relative h-full w-full">
      <svg
        viewBox="0 0 1088 194"
        preserveAspectRatio="none"
        className="absolute inset-0 h-full w-full overflow-visible"
        aria-hidden="true"
      >
        <defs>
          <marker
            id={`barrier-head-${direction}`}
            viewBox="0 0 10 10"
            refX="8"
            refY="5"
            markerWidth="8"
            markerHeight="8"
            orient="auto-start-reverse"
          >
            <path d="M 0,0 L 10,5 L 0,10 z" fill="#dc2626" />
          </marker>
        </defs>
        <line
          x1="544"
          y1="97"
          x2="950"
          y2={isDown ? 215 : -21}
          stroke="#dc2626"
          strokeWidth="2.5"
          strokeDasharray="10,6"
          markerEnd={`url(#barrier-head-${direction})`}
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      <div className="absolute left-[calc(50%+22px)] top-1/2 w-[400px] -translate-y-1/2 rounded-2xl border-[3px] border-red-600 bg-white/90 p-4 text-left text-sm leading-snug text-red-600 md:text-base">
        <p className="font-bold">Obstacle(s) to Competition</p>
        <p className="mt-1">{label}</p>
      </div>
    </div>
  );
}

function FutureArrow() {
  return (
    <div className="flex h-full w-full items-center justify-center">
      <div className="flex w-full items-center">
        {/* Left stem */}
        <div className="h-7 w-5 shrink-0 bg-blue-600 md:h-8 md:w-6"></div>

        {/* Rounded label box — min-width so text wraps to 2 lines */}
        <div className="min-w-[200px] rounded-2xl border-[3px] border-blue-600 bg-white px-3 py-3 md:min-w-[240px] md:border-4 md:px-4 md:py-4">
          <p className="text-balance text-center text-sm font-bold leading-tight text-blue-600 md:text-base">
            The future of AI-fueled mobile app marketing.
          </p>
        </div>

        {/* Right stem — connects box to arrowhead */}
        <div className="h-7 w-5 shrink-0 bg-blue-600 md:h-8 md:w-6"></div>

        {/* Triangle arrowhead — large, extends well beyond box vertically */}
        <div className="h-0 w-0 shrink-0 border-y-[36px] border-l-[40px] border-y-transparent border-l-blue-600 md:border-y-[44px] md:border-l-[48px]"></div>
      </div>
    </div>
  );
}

function Card({
  title,
  examples,
  headline,
  children,
  footer,
  emphasized,
  logo,
}: {
  title: string;
  examples?: string;
  headline: string;
  children: ReactNode;
  footer?: string;
  emphasized?: boolean;
  logo?: boolean;
}) {
  return (
    <div
      className={`flex h-full flex-col rounded-xl px-5 py-2.5 shadow-sm md:px-6 md:py-2.5 ${
        emphasized
          ? "bg-blue-50 ring-2 ring-blue-400"
          : "bg-white ring-1 ring-slate-200"
      }`}
    >
      <h3 className="flex items-center gap-2.5 text-xl font-bold leading-tight tracking-tight text-blue-600 md:gap-3 md:text-3xl">
        {logo ? (
          <img
            // The icon is a public/ asset, so it lives under Vite's base
            // ("/admin/") in dev AND prod — the worker's /admin branch strips
            // the base and hits ASSETS. A hand-written "/capital/brand/..."
            // path 200s on the SPA fallback and renders as a broken image.
            src={`${import.meta.env.BASE_URL.replace(/\/$/, "")}/img/brand/a7y-icon.png`}
            alt=""
            aria-hidden="true"
            className="h-7 w-7 shrink-0 md:h-9 md:w-9"
          />
        ) : null}
        {title}
      </h3>
      <p className="text-base italic leading-snug text-slate-600 md:text-xl">
        {headline}
      </p>
      {examples ? (
        <p className="text-xs text-slate-500 md:text-sm">{examples}</p>
      ) : null}
      <ul className="mt-2 space-y-0.5 pl-3 text-sm leading-snug text-slate-600 md:mt-2 md:pl-4 md:text-base">
        {children}
      </ul>
      {footer ? (
        <p className="pt-2 text-sm italic leading-snug text-slate-500 md:pt-3 md:text-base">
          {footer}
        </p>
      ) : null}
    </div>
  );
}

function Bullet({
  tone = "neutral",
  children,
}: {
  tone?: "neutral" | "pro" | "con";
  children: ReactNode;
}) {
  const marker =
    tone === "pro"
      ? { glyph: "+", className: "text-emerald-600 font-bold" }
      : tone === "con"
        ? { glyph: "−", className: "text-rose-500 font-bold" }
        : { glyph: "→", className: "text-blue-600" };
  return (
    <li className="flex gap-2">
      <span aria-hidden="true" className={`shrink-0 ${marker.className}`}>
        {marker.glyph}
      </span>
      <span>{children}</span>
    </li>
  );
}

function Lead({ children }: { children: ReactNode }) {
  return <span className="font-semibold text-slate-900">{children}</span>;
}
